// 全局提示服务（替代 antd 的 message 静态方法）——与渲染组件分离，便于在
// 任意非组件模块（utils/拦截器/事件回调）里直接调用，同时保持 ToastHost 的 HMR 边界干净。

export type ToastKind = "success" | "error" | "info" | "warning";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** 毫秒；<=0 表示不自动消失（需手动关闭）。默认 2400，撤销类 5000 */
  duration?: number;
  action?: ToastAction;
}

export interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
  duration: number;
  action?: ToastAction;
}

let seq = 0;
let pending: ToastItem[] = [];
let sink: ((item: ToastItem) => void) | null = null;

/**
 * 由 <ToastHost/> 在挂载时注册渲染回调；返回解除注册函数。
 * 返回解除回调是必要的：React 严格模式下 effect 会执行两次，注册前需先清空上一次。
 */
export function registerToastSink(next: ((item: ToastItem) => void) | null): void {
  sink = next;
  if (next && pending.length) {
    const queued = pending;
    pending = [];
    queued.forEach(next);
  }
}

function push(kind: ToastKind, text: string, options?: ToastOptions) {
  const item: ToastItem = {
    id: ++seq,
    kind,
    text,
    duration: options?.duration ?? 2400,
    action: options?.action,
  };
  // 早于 <ToastHost/> 挂载的调用先入队，挂载后统一回放（例如启动期的失败提示）
  if (sink) sink(item);
  else pending.push(item);
}

export const toast = {
  success: (text: string, options?: ToastOptions) => push("success", text, options),
  error: (text: string, options?: ToastOptions) => push("error", text, options),
  info: (text: string, options?: ToastOptions) => push("info", text, options),
  warning: (text: string, options?: ToastOptions) => push("warning", text, options),
  /** 可撤销提示：默认 5 秒内可点「撤销」回滚——写操作（删除/驳回/状态变更）统一走这里 */
  undo: (text: string, onUndo: () => void, options?: ToastOptions) =>
    push("success", text, {
      duration: 5000,
      action: { label: "撤销", onClick: onUndo },
      ...options,
    }),
};
